import { useEffect, useState } from "react"
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts"
import { reportService } from "../../services/reportService"
import { farmerService } from "../../services/farmerService"
import { DashboardStats, TreeByRegion, TreeByType, Farmer } from "../../types"

// ── Helpers ───────────────────────────────────────────────────────────────────
const regionName = (r: Farmer["region"]) =>
  typeof r === "object" && r !== null ? (r as any).name : r

// Distinct colors for region pie
const REGION_COLORS = [
  "#10b981", "#34d399", "#6ee7b7", "#a7f3d0",
  "#059669", "#047857", "#065f46", "#d1fae5",
  "#ecfdf5", "#6d28d9",
]

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({
  label, value, icon, accent = "#10b981",
}: {
  label: string
  value: number | string
  icon: React.ReactNode
  accent?: string
}) {
  return (
    <div className="relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5 overflow-hidden group hover:border-white/[0.12] transition-all">
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(circle at 0% 0%, ${accent}18 0%, transparent 60%)` }}
      />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-xs font-medium tracking-widest uppercase mb-2">{label}</p>
          <p className="text-white text-3xl font-black tracking-tight">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          {icon}
        </div>
      </div>
    </div>
  )
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-xs shadow-xl">
      {label && <p className="text-neutral-400 mb-1">{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? p.fill ?? "#10b981" }} className="font-bold">
          {p.value?.toLocaleString()} trees
        </p>
      ))}
    </div>
  )
}

function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-xs shadow-xl">
      <p className="text-neutral-400 mb-0.5">{p.name}</p>
      <p style={{ color: p.payload.fill }} className="font-bold">
        {p.value?.toLocaleString()} trees
      </p>
      <p className="text-neutral-600">
        {((p.percent ?? 0) * 100).toFixed(1)}%
      </p>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [stats,    setStats]    = useState<DashboardStats | null>(null)
  const [byRegion, setByRegion] = useState<TreeByRegion[]>([])
  const [byType,   setByType]   = useState<TreeByType[]>([])
  const [farmers,  setFarmers]  = useState<Farmer[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r, t, f] = await Promise.all([
          reportService.getStats(),
          reportService.getTreesByRegion(),
          reportService.getTreesByType(),
          farmerService.getAll({ per_page: 8, page: 1 }),
        ])
        setStats(s)
        setByRegion(r)
        setByType(t)
        setFarmers(f.data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-neutral-500">
          <svg className="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm">Loading dashboard...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Total Farmers"
          value={stats?.total_farmers ?? 0}
          accent="#10b981"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        <StatCard
          label="Total Trees"
          value={stats?.total_trees ?? 0}
          accent="#34d399"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c-1.5 3-4 5-4 8a4 4 0 008 0c0-3-2.5-5-4-8z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v10M9 18l3 3 3-3" />
            </svg>
          }
        />
        <StatCard
          label="Tree Types"
          value={stats?.total_tree_types ?? 0}
          accent="#6ee7b7"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
        />
        <StatCard
          label="Active Regions"
          value={stats?.active_regions ?? 0}
          accent="#a7f3d0"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          }
        />
      </div>

      {/* ── Bar Chart — full width ── */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
        <p className="text-white text-sm font-bold mb-1">Trees by Region</p>
        <p className="text-neutral-600 text-xs mb-5">Total tree count per region</p>
        {byRegion.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-neutral-700 text-sm">No data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byRegion} barCategoryGap="30%">
              <XAxis
                dataKey="region"
                tick={{ fill: "#6b7280", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#6b7280", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {byRegion.map((_, i) => (
                  <Cell key={i} fill={`hsl(${158 + i * 8}, ${60 - i * 2}%, ${45 + i * 2}%)`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Two Pie Charts ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Trees by Type — donut */}
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
          <p className="text-white text-sm font-bold mb-1">Trees by Species</p>
          <p className="text-neutral-600 text-xs mb-4">Distribution across tree types</p>
          {byType.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-neutral-700 text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byType}
                  dataKey="total"
                  nameKey="tree_type"
                  cx="50%"
                  cy="45%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {byType.map((entry, i) => (
                    <Cell key={i} fill={entry.color_hex ?? `hsl(${158 + i * 15}, 55%, 45%)`} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(value) => (
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Trees by Region — pie */}
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
          <p className="text-white text-sm font-bold mb-1">Trees by Region</p>
          <p className="text-neutral-600 text-xs mb-4">Regional share of total tree count</p>
          {byRegion.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-neutral-700 text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={byRegion}
                  dataKey="total"
                  nameKey="region"
                  cx="50%"
                  cy="45%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {byRegion.map((_, i) => (
                    <Cell key={i} fill={REGION_COLORS[i % REGION_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  formatter={(value) => (
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Top Regions List ── */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
        <p className="text-white text-sm font-bold mb-1">Region Breakdown</p>
        <p className="text-neutral-600 text-xs mb-4">Tree count and share per region</p>
        {byRegion.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-neutral-700 text-sm">No data yet</div>
        ) : (() => {
          const total = byRegion.reduce((s, r) => s + Number(r.total), 0)
          return (
            <div className="space-y-3">
              {byRegion.map((r, i) => {
                const pct = total > 0 ? (Number(r.total) / total) * 100 : 0
                const color = REGION_COLORS[i % REGION_COLORS.length]
                return (
                  <div key={r.region}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-neutral-300 text-xs font-medium">{r.region}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-neutral-500 text-xs">{Number(r.total).toLocaleString()} trees</span>
                        <span className="text-xs font-bold" style={{ color }}>{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })()}
      </div>

      {/* ── Recent Farmers Table ── */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-bold">Recent Farmers</p>
            <p className="text-neutral-600 text-xs mt-0.5">Latest 8 registrations</p>
          </div>
          <a
            href="/admin/farmers"
            className="text-emerald-400 hover:text-emerald-300 text-xs font-medium transition-colors"
          >
            View all →
          </a>
        </div>

        {farmers.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-neutral-700 text-sm">
            No farmers registered yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  {["Code", "Name", "Phone", "Region", "Trees", "Registered"].map(h => (
                    <th
                      key={h}
                      className="px-5 py-3 text-left text-[11px] font-semibold text-neutral-600 tracking-wider uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {farmers.map((farmer, i) => (
                  <tr
                    key={farmer.id}
                    className={`border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors ${
                      i === farmers.length - 1 ? "border-none" : ""
                    }`}
                  >
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-emerald-400/80 text-xs bg-emerald-500/5 px-2 py-0.5 rounded-md">
                        {farmer.farmer_code ?? "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-white font-medium">{farmer.name}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{farmer.phone}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{regionName(farmer.region)}</td>
                    <td className="px-5 py-3.5">
                      <span className="text-emerald-400 font-bold">
                        {farmer.trees?.reduce((sum, t) => sum + (Number(t.count) || 0), 0) ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-600 text-xs">
                      {farmer.created_at
                        ? new Date(farmer.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit", month: "short", year: "numeric",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  )
}